import { Opportunity, OpportunitySaving, OpportunityMetric, Plot } from "@/opportunity/opportunity";
import React, { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import ConditionalStaffOnly from "@/opportunity/opportunity_detail_page/ConditionalStaffOnly";
import CompressorInletOutletPowerChart from "./CompressorInletOutletPowerChart";
import { TemperatureAnnotation } from "./GenericRawDataChart";

const SavingCard = ({ saving }: {
  saving: OpportunitySaving,
}) => {
  return (
    <div
      className="border rounded-lg bg-white p-2 text-center flex flex-shrink flex-col justify-center items-center"
    >
      <div>
        {saving.name}
      </div>
      <div>
        {
          saving.unit === "$" ? (
            <span className="text-2xl">{saving.unit}{saving.value.toLocaleString()}</span>
          ) : (
            <div>
              <span className="text-2xl">{saving.value.toLocaleString()}</span>
              <br/>
              <span>{saving.unit}</span>
            </div>
          )
        }
      </div>
    </div>
  );
};


const OpportunityDetectionsTimeline = ({ 
  opportunity, 
  showStaffView 
}: { 
  opportunity: Opportunity;
  showStaffView: boolean;
}) => {
  const orderedDetections = opportunity.ordered_detections || [];
  const [livePlotSuccessful, setLivePlotSuccessful] = useState<boolean | null>(null);

  // Find the index of the highest severity detection in orderedDetections (so it shows by default):
  const defaultIndex = useMemo(() => {
    if (!orderedDetections.length || !opportunity.highest_severity_instance) return 0;
    const highestDt = opportunity.highest_severity_instance?.detection_datetime;
    if (!highestDt) return 0;
    const idx = orderedDetections.findIndex(det => det.detection_datetime === highestDt);
    return idx >= 0 ? idx : 0;
  }, [opportunity.highest_severity_instance, orderedDetections]);

  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const currentDetection = orderedDetections[currentIndex] || null;

  // Reset live plot success state when detection changes
  useEffect(() => {
    setLivePlotSuccessful(null);
  }, [currentIndex]);

  // Detection History Summary (visible to all)
  return (
    <div className="flex flex-col gap-4">
      <div>
        <ScrollArea>
          <div className="relative flex items-center pb-4 mb-4 min-w-full">
            <div
              className="absolute top-2.5 left-0 h-1 bg-gray-300 min-w-full"
              style={{ width: `${orderedDetections.length * 50}px`}}
            />

            {/* Ticks and Labels */}
            {orderedDetections.map((detection, index) => {
              const isHighestSeverity =
                opportunity.highest_severity_instance && 
                detection.detection_datetime === opportunity.highest_severity_instance.detection_datetime;

              const showLabel =
                index === 0 || index === orderedDetections.length - 1 || index % 5 === 0;

              return (

                <div
                  key={index}
                  className="flex flex-col items-center relative grow-0 shrink-0 basis-[50px]"
                >

                  {/* Tick Marker */}
                  <div
                    className={`w-0.5 h-6 ${
                      isHighestSeverity
                        ? 'bg-pink-500' // Pink for highest severity
                        : index === currentIndex
                          ? 'bg-blue-500' // Blue for active
                          : 'bg-gray-400' // Default
                    }`}
                  />

                  {/* Conditional Date Label */}
                  {showLabel && (
                    <span
                      className={`absolute -bottom-6 text-xs mt-2 ${
                        isHighestSeverity
                          ? 'text-pink-500 font-semibold'
                          : index === currentIndex
                            ? 'text-blue-500 font-semibold'
                            : 'text-gray-600'
                      }`}
                    >
                                         {format(parseISO(detection.detection_datetime), "MM/dd/yy")}
                                        </span>
                  )}

                  {/* Clickable Area */}
                  <button
                    onClick={() => {
                      setCurrentIndex(index);
                      // Trigger highlight in compressor temp chart
                      if ((window as any).__highlightDetectionTime) {
                        (window as any).__highlightDetectionTime(parseISO(detection.detection_datetime), index);
                      }
                    }}
                    className="absolute top-0 left-0 w-full h-full focus:outline-none"
                    aria-label={`Go to Detection ${index + 1}`}
                  />
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal"/>
        </ScrollArea>


        {/* Navigation Controls */}
        {orderedDetections.length > 0 && (
          <div className="flex justify-between mt-4">
            <Button
              variant="outline"
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((prev) => prev - 1)}
            >
              Previous
            </Button>
            <p className="text-sm">
              Detection {currentIndex + 1} of {orderedDetections.length}
            </p>
            <Button
              variant="outline"
              disabled={currentIndex === orderedDetections.length - 1}
              onClick={() => setCurrentIndex((prev) => prev + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {!orderedDetections.length ? (
        <p className="text-sm text-gray-500">No detections found.</p>
      ) : (
        <div className="space-y-4">
          {currentDetection && (
            <Card className="p-4">
              <ConditionalStaffOnly showStaffView={showStaffView}>
                <p className="font-semibold mb-2">
                  Detection Time: {format(parseISO(currentDetection.detection_datetime), "MM/dd/yy")}
                </p>
                {
                  currentDetection.detection_reason && (
                    <p className="font-semibold mb-2">
                      Detection Reason: {currentDetection.detection_reason}
                    </p>
                  )
                }
              </ConditionalStaffOnly>
              {/* Metrics */}
              {currentDetection.metrics?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {currentDetection.metrics
                    .filter((metric: OpportunitySaving) => 
                      !metric.name.toLowerCase().includes('times triggered') && 
                      !metric.name.toLowerCase().includes('# of times') &&
                      !metric.name.toLowerCase().includes('total runtime')
                    )
                    .map((metric: OpportunitySaving, idx: number) => (
                      <SavingCard key={idx} saving={metric}/>
                    ))}
                </div>
              )}
              {/* Plots */}
              <div className="mt-6">
                {/* Check if we should show live plot */}
                {(() => {
                  const detectorKey = (opportunity.category_object as any)?.detector_key;
                  const hasPlotTimes = currentDetection.plot_start_time && currentDetection.plot_end_time && 
                    opportunity.installation?.id && opportunity.organization_id;
                  
                  // Determine annotations and chart options based on failure pattern
                  let annotations: TemperatureAnnotation[] = [];
                  let showAmbient = false;
                  
                  if (detectorKey === 'refrigerant_undercharged__undercharged') {
                    // Compressor overheating: show warning and critical lines
                    // Match Athena chart logic: predicted_max_temp_c is warning, critical = warning * 1.2
                    const predictedMaxTempMetric = currentDetection.metrics?.find((m: OpportunityMetric) => 
                      m.name.toLowerCase().includes('critical compressor temperature') ||
                      m.name.toLowerCase().includes('predicted max') ||
                      m.name.toLowerCase().includes('predicted_max')
                    );
                    
                    if (predictedMaxTempMetric) {
                      // Check if metric is in Celsius or Fahrenheit
                      const isCelsius = predictedMaxTempMetric.unit?.toLowerCase().includes('c') && 
                                       !predictedMaxTempMetric.unit?.toLowerCase().includes('f');
                      
                      // Convert to Fahrenheit if needed (matching Athena chart logic)
                      const warningTempF = isCelsius 
                        ? (predictedMaxTempMetric.value * 9/5 + 32)
                        : predictedMaxTempMetric.value;
                      
                      // Convert to Celsius, multiply by 1.3, convert back to Fahrenheit
                      const warningTempC = (warningTempF - 32) * 5/9;
                      const criticalTempF = (warningTempC * 1.3) * 9/5 + 32;
                      
                      annotations = [
                        { name: 'Warning Zone', value: warningTempF, color: '#f59e0b', lineStyle: 'dashed' },
                        { name: 'Critical Zone', value: criticalTempF, color: '#ef4444', lineStyle: 'dashed' }
                      ];
                    }
                  } else if (detectorKey === 'suction_temp_less_than_freezing') {
                    // Freezing suction: show ambient temp and freezing line
                    showAmbient = true;
                    annotations = [
                      { name: 'Freezing (32°F)', value: 32, color: '#ef4444', lineStyle: 'dashed' }
                    ];
                  }
                  // For other failure patterns (e.g., short cycling), no annotations needed
                  
                  if (!hasPlotTimes) {
                    // No plot times available, show static plots
                    return currentDetection.plots
                      .filter((plot: Plot) => !plot.filepath?.includes('_debug'))
                      .map((plot: Plot, index: number) => (
                        <Card
                          key={index}
                          className="p-6 mb-6 col-span-12 lg:col-span-6 whitespace-pre-line"
                        >
                          <p className="font-barlow font-semibold text-lg">
                            {plot.title}
                          </p>
                          <p className="mb-2">{plot.description}</p>
                          {plot.filepath && (
                            <a href={plot.filepath} target="_blank" rel="noopener noreferrer">
                              <img
                                src={plot.filepath}
                                alt={plot.title}
                                className="w-full object-cover rounded-lg"
                              />
                            </a>
                          )}
                        </Card>
                      ));
                  }
                  
                  // If no data available, show static plots instead of chart
                  if (livePlotSuccessful === false) {
                    return currentDetection.plots
                      .filter((plot: Plot) => !plot.filepath?.includes('_debug'))
                      .map((plot: Plot, index: number) => (
                        <Card
                          key={index}
                          className="p-6 mb-6 col-span-12 lg:col-span-6 whitespace-pre-line"
                        >
                          <p className="font-barlow font-semibold text-lg">
                            {plot.title}
                          </p>
                          <p className="mb-2">{plot.description}</p>
                          {plot.filepath && (
                            <a href={plot.filepath} target="_blank" rel="noopener noreferrer">
                              <img
                                src={plot.filepath}
                                alt={plot.title}
                                className="w-full object-cover rounded-lg"
                              />
                            </a>
                          )}
                        </Card>
                      ));
                  }
                  
                  // Use unified chart for all failure patterns with appropriate annotations
                  return (
                    <CompressorInletOutletPowerChart
                      installationId={opportunity.installation.id.toString()}
                      organizationId={opportunity.organization_id}
                      plotStartTime={currentDetection.plot_start_time}
                      plotEndTime={currentDetection.plot_end_time}
                      locationName={opportunity.location.name || ''}
                      sensorKitSerialNumber={opportunity.installation.sensor_kit || 'Unknown'}
                      annotations={annotations}
                      showAmbient={showAmbient}
                      onSuccess={(hasData) => setLivePlotSuccessful(hasData)}
                    />
                  );
                })()}
                
                {/* Debug plots - Only show if staff view is enabled */}
                <ConditionalStaffOnly showStaffView={showStaffView}>
                  {currentDetection && currentDetection.plots
                    .filter((plot: Plot) => plot.filepath?.includes('_debug'))
                    .map((plot: Plot, index: number) => (
                      <Card
                        key={`debug-${index}`}
                        className="p-6 mb-6 col-span-12 lg:col-span-6 whitespace-pre-line border-orange-200 bg-orange-50"
                      >
                        <p className="font-barlow font-semibold text-lg text-orange-800">
                          {plot.title} (Debug)
                        </p>
                        <p className="mb-2 text-orange-700">{plot.description}</p>
                        {plot.filepath && (
                          <a href={plot.filepath} target="_blank" rel="noopener noreferrer">
                            <img
                              src={plot.filepath}
                              alt={plot.title}
                              className="w-full object-cover rounded-lg"
                            />
                          </a>
                        )}
                      </Card>
                    ))}
                </ConditionalStaffOnly>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default OpportunityDetectionsTimeline;